import { useCallback, useEffect, useRef } from "react"

/**
 * Calls the function `debounce` milliseconds after the last time the function
 * is called (trailing debounce). Any change to deps will reset the timer, not
 * calling the function.
 */
const useDebounce = <T extends unknown[]>(
  callback: (...args: T) => void,
  debounce: number
): [(...args: T) => void, () => void] => {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null)
  const cb = useRef<(...args: T) => void>(callback)

  useEffect(() => {
    cb.current = callback
  }, [callback])

  const cancel = useCallback(() => {
    if (timeout.current !== null) {
      clearTimeout(timeout.current)
    }
  }, [])

  useEffect(() => {
    cancel()
  }, [debounce, cancel])

  const debounced = useCallback(
    (...args: T) => {
      cancel()

      timeout.current = setTimeout(() => {
        cb.current(...args)
      }, debounce)
    },
    [debounce, cancel]
  )

  return [debounced, cancel]
}

export default useDebounce
